/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { getTodayScheduleAsync } from '../../actions/DataActionExtension.g';

import { isMobile } from '../../components/breakpoints';

import { IEvent, Event } from './components/event';

import errors from '../../resources/modules/errors.json';

import { IEcommLiveStreamData } from './ecomm-live-stream.data';
import { IStreamsData, IEcommLiveStreamProps } from './ecomm-live-stream.props.autogenerated';

var moment = require('moment-timezone');

export interface IEcommLiveStreamViewProps extends IEcommLiveStreamProps<IEcommLiveStreamData> {
  title: string;
  floorPrompt: string;
  anchorID: string;
  streams: IStreamsData[];
  noScheduleFeedback: string;
  events: React.ReactNode[];
  mobileMode: boolean;
}

/**
 *
 * EcommLiveStream component
 * @extends {React.PureComponent<IEcommLiveStreamProps<IEcommLiveStreamData>>}
 */
class EcommLiveStream extends React.PureComponent<IEcommLiveStreamProps<IEcommLiveStreamData>> {
  public state: any = { width: 0, rawData: [], time: moment.tz('Asia/Singapore') };

  private _interval = 300000;
  private _timeID: any;

  public async componentDidMount() {
    const { config } = this.props;

    const data = await getTodayScheduleAsync({ callerContext: this.props.context.actionContext }, config.env, config.entity, config.containerName, config.blobName, config.libraryName);

    if (this.state.rawData.length === 0) {
      this.setState({ rawData: data });
    }

    this._timeID = setInterval(() => this.setState({ time: moment.tz('Asia/Singapore') }), this._interval);
    
    if (window !== undefined) {
      window.addEventListener('resize', this._updateDimensions);

      this.setState({ width: window.innerWidth });
    }
  }

  public render(): JSX.Element | null {
    if (this.state.rawData.length === 0) {
      return null;
    }

    const { config } = this.props;

    const EcommLiveStreamViewProps = {
      ...this.props,
      title: config.title,
      floorPrompt: config.floorPrompt,
      anchorID: config.anchorID,
      streams: config.streams,
      noScheduleFeedback: config.noScheduleMsg,
      events: this._getSchedule(),
      mobileMode: isMobile(this.state.width)
    };

    return this.props.renderView(EcommLiveStreamViewProps);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this._updateDimensions);

    clearInterval(this._timeID);
  }

  private _getSchedule(): JSX.Element[] | null {
    const { config } = this.props;

    if (this.state.rawData && this.state.rawData.length !== 0) {
      const now = this.state.time.format('hh:mm A');

      const errorIdx = this.state.rawData.findIndex((error: String) => error === errors.sutraNoSchedule || error === errors.sutraNoDictionary);

      if (errorIdx !== -1) {
        return null;
      }

      let events = this.state.rawData.map((item: string, index: number) => {  
        let record: IEvent = JSON.parse(item);

        let active = false;

        if (record.Time) {
          const current = this._isCurrentTimeLarger(now, record.Time);

          if (index === this.state.rawData.length - 1) {
            const end = this._isCurrentTimeLarger(now, config.closingTime);

            active = current && !end ? true : false;
          } else {
            if (this.state.rawData[index + 1]) {
              let nxtime = JSON.parse(this.state.rawData[index + 1]).Time;

              if (nxtime === '') {
                const checkTime = (slot: string) => JSON.parse(slot).Time !== '';
                const subArray = this.state.rawData.slice(index + 1);
                const idx = subArray.findIndex(checkTime);

                nxtime = JSON.parse(subArray[`${idx}`]).Time;
              }

              let next = this._isCurrentTimeLarger(now, nxtime);

              active = current && !next ? true : false;
            }
          }
        }

        const url = record.SutraUrl ? `${config.issuu}/${record.SutraUrl}/${record.Page}` : '';

        return <Event
          key={index}
          Time={record.Time ? record.Time : ''}
          EnglishName={record.EnglishName}
          ChineseName={record.ChineseName}
          SutraUrl={url}
          Page={record.Page}
          buttonText={config.sutraButtonText ? config.sutraButtonText : ''}
          active={active}
          locale={this.props.context.request.locale}
          mobileMode={isMobile(this.state.width)}
        />;
      });

      return events;
    }

    return null;
  }

  private _isCurrentTimeLarger(currTime: string, eventTime: string): boolean {
    // Returns true if current time is larger or equal to event time.

    const currUnit = currTime.indexOf('AM') !== -1 ? 0 : 1;
    const eventUnit = eventTime.indexOf('AM') !== -1 ? 0 : 1;

    if (currUnit > eventUnit) {
      return true;
    }

    if (currUnit < eventUnit) {
      return false;
    }

    // Compare hour when both current and event are in the same units.

    let currHour = parseInt(currTime.substring(0, currTime.indexOf(':')));
    let eventHour = parseInt(eventTime.substring(0, eventTime.indexOf(':')));

    currHour = currHour === 12 ? 0 : currHour;
    eventHour = eventHour === 12 ? 0 : eventHour;

    if (currHour > eventHour) {
      return true;
    }

    // Compare minutes when both current and event are in the same units and same hour.

    if (currHour === eventHour) {
      const currMin = parseInt(currTime.substring(currTime.indexOf(':') + 1, currTime.indexOf(' ')));
      const eventMin = parseInt(eventTime.substring(eventTime.indexOf(':') + 1, eventTime.indexOf(' ')));

      if (currMin > eventMin) {
        return true;
      }
    }

    return false;
  }

  private _updateDimensions = () => {
    this.setState({ width: window.innerWidth });
  }; 
}

export default EcommLiveStream;